import { gql } from '@apollo/client'

/**
 * Fields a directory row needs on a card or a ficha. Deliberately NOT
 * `CARD_LOCATION_FIELDS`: that fragment selects photos, review scores,
 * pricing, availability, discounts and `configSummary` — none of which exists
 * on an unclaimed row, all of which would be dead weight in the payload, and
 * several of which (`urlRoute`) resolve to a URL the directory must never link.
 */
export const DIRECTORY_CARD_FIELDS = gql`
  fragment DirectoryCardFields on Location {
    id
    title
    address
    cardLocation
    shortAddress
    isDirectoryListing
    coordinates {
      lat
      lng
    }
    addressDetails {
      city
      state
    }
    defaultConfiguration {
      id
      locationType
    }
    directoryMeta {
      brandColor
      chain
      glyph
      hours
      logoUrl
      monogram
      priceExcludesIva
      priceFromMxn
      sizes
    }
  }
`

/**
 * The SECOND, explicit search query (plan §2.1). Its results are rendered in
 * their own list below the tier-floor divider and are NEVER merged into the
 * ranked `searchLocations` array — keeping them in separate result sets is
 * what stops a future scoring change from promoting an unclaimed facility
 * above monetized supply.
 */
export const DIRECTORY_LOCATIONS = gql`
  ${DIRECTORY_CARD_FIELDS}
  query DirectoryLocations(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
  ) {
    directoryLocations(pagination: $pagination, searchInput: $searchInput) {
      total
      hits {
        ...DirectoryCardFields
      }
    }
  }
`

/**
 * The zero-supply city guide's SSR read. Same rows as `DIRECTORY_LOCATIONS`
 * plus `directoryHasContact`, which decides whether a card offers "Ver
 * teléfono". Kept off the lazy floor's query: on a search hit the server has
 * to load each row from DynamoDB to answer it (ES never holds the contact
 * bundle), and only the guide renders the button.
 */
export const DIRECTORY_GUIDE_LOCATIONS = gql`
  ${DIRECTORY_CARD_FIELDS}
  query DirectoryGuideLocations(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
  ) {
    directoryLocations(pagination: $pagination, searchInput: $searchInput) {
      total
      hits {
        ...DirectoryCardFields
        directoryHasContact
      }
    }
  }
`

/**
 * Does the facility's area have ANY verified SpotMe supply? The ficha uses the
 * count alone to decide whether "Ver espacios verificados" leads somewhere —
 * in a zero-supply city it would land on a page with none.
 */
export const VERIFIED_NEARBY_COUNT = gql`
  query VerifiedNearbyCount(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
  ) {
    searchLocations(pagination: $pagination, searchInput: $searchInput) {
      total
    }
  }
`

/** Ficha read — the public branch of `Query.location` (plan §1.8). */
export const DIRECTORY_LOCATION = gql`
  ${DIRECTORY_CARD_FIELDS}
  query DirectoryLocation($locationId: String!) {
    location(locationId: $locationId) {
      ...DirectoryCardFields
      directoryHasContact
      directorySources
      priceCapturedAt
      priceSource
    }
  }
`

export const REVEAL_DIRECTORY_CONTACT = gql`
  mutation RevealDirectoryContact($input: DirectoryRevealInput!) {
    revealDirectoryContact(input: $input) {
      status
      revealsRemaining
      contact {
        email
        phone
        website
        whatsapp
      }
    }
  }
`

/**
 * The sitemap's roster (2026-08-25 noindex lift): every live directory row,
 * selecting ONLY what routing + the indexability gate need. Consumed by
 * `pages/sitemap/search.xml.tsx`, which filters through
 * `isDirectoryFichaIndexable` and builds hrefs with `buildDirectoryFichaHref`
 * — the same two functions the ficha itself uses, so the sitemap can never
 * disagree with a page's own `noindex`.
 */
export const DIRECTORY_SITEMAP_ROWS = gql`
  query DirectorySitemapRows {
    directorySitemapRows {
      id
      title
      coordinates {
        lat
        lng
      }
      addressDetails {
        city
      }
      defaultConfiguration {
        id
        locationType
      }
      directoryHasContact
      directoryMeta {
        hours
        logoUrl
        priceFromMxn
      }
    }
  }
`
